import { devIdentityName } from '~/lib/onboarding-gate';

/**
 * FRONT-1037: what the dev skip switch writes in place of the onboarding
 * wizard, so a fresh local account lands in a working app rather than one
 * that merely got past the redirect.
 *
 * The wizard's only REQUIRED answer is the name (it creates the self-entity
 * the onboarded gate reads). Its finish also quietly persists the browser
 * timezone and, for an admin, switches the custom agent on — a fresh org
 * starts every agent inactive, and chat then fails outright — so the skip
 * does both too, with the same best-effort semantics the wizard gives them.
 * Finally it stamps the wizard-completed marker so the home feed doesn't
 * nag, and reads the onboarded answer back: a skip is only done when the
 * server agrees, never on the strength of the writes alone.
 *
 * Throws when the identity write fails or the read-back does not say
 * onboarded; the caller then opens the real wizard.
 */
export interface DevAutoOnboardDeps {
  patchIdentity: (displayName: string) => Promise<unknown>;
  saveTimezone: (timezone: string) => Promise<unknown>;
  ensureCustomAgentEnabled: () => Promise<void>;
  markWizardCompleted: () => Promise<unknown>;
  readOnboarded: () => Promise<{ onboarded: boolean; displayName?: string }>;
}

export async function runDevAutoOnboard(input: {
  me: { name?: string; email?: string; isAdmin?: boolean } | undefined;
  timezone: string;
  deps: DevAutoOnboardDeps;
}): Promise<{ onboarded: boolean; displayName?: string }> {
  const { deps } = input;
  await deps.patchIdentity(devIdentityName(input.me));
  await deps.saveTimezone(input.timezone).catch(() => undefined);
  if (input.me?.isAdmin === true) {
    await deps.ensureCustomAgentEnabled().catch(() => undefined);
  }
  await deps.markWizardCompleted().catch(() => undefined);
  const after = await deps.readOnboarded();
  if (after.onboarded !== true) {
    throw new Error('identity saved but the server still reports not onboarded');
  }
  return after;
}
